import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FILTER_RESOURCE_ADMINISTRATIVE_DISTRICT = 'administrative-district';

export const BD_ADMINISTRATIVE_DISTRICT_IDS_CONDITION_ID =
  'administrativeDistrictId';

const ADMINISTRATIVE_DISTRICTS_DETAIL_LIMIT = 10;

export const FilterConditionAdministrativeDistrictIdPluginType: FilterConditionPluginSpecification<
  BdAdministrativeDistrictId[],
  FilterConditionAdministrativeDistrictIdDto,
  BdAdministrativeDistrictIdCondition
> = {
  type: 'administrativeDistrictIds',
  category: 'person',
  displayName: { de: 'Landkreise' },
  sortingIndex: 4,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionAdministrativeDistrictIdDto,
  ): FilterConditionPluginImpl<BdAdministrativeDistrictId[]> {
    return new FilterConditionAdministrativeDistrictIdPlugin(
        obj.administrativeDistrictIds.map((it) =>
          BdAdministrativeDistrictId.create(it),
        ),
      );
  },
  fromCondition(
    jsonObj: BdAdministrativeDistrictIdCondition,
  ): FilterConditionPluginImpl<BdAdministrativeDistrictId[]> {
    return new FilterConditionAdministrativeDistrictIdPlugin(
        jsonObj?.includes?.map((it) => BdAdministrativeDistrictId.create(it)),
      );
  },
  newInstance(): FilterConditionPluginImpl<BdAdministrativeDistrictId[]> {
    return new FilterConditionAdministrativeDistrictIdPlugin([]);
  },
};

export class FilterConditionAdministrativeDistrictIdPlugin extends FilterConditionPluginImpl<
  BdAdministrativeDistrictId[],
  FilterConditionAdministrativeDistrictIdDto
> {
  static override Specification: FilterConditionPluginSpecification<
    BdAdministrativeDistrictId[],
    FilterConditionAdministrativeDistrictIdDto,
    BdAdministrativeDistrictIdCondition
  > = FilterConditionAdministrativeDistrictIdPluginType;

  constructor(private administrativeDistrictIds: BdAdministrativeDistrictId[]) {
    super(FilterConditionAdministrativeDistrictIdPluginType.type);
  }

  clone(): FilterConditionAdministrativeDistrictIdPlugin {
    return new FilterConditionAdministrativeDistrictIdPlugin([
      ...this.administrativeDistrictIds.map((it) => it.clone()),
    ]);
  }

  override equals(
    other: FilterConditionAdministrativeDistrictIdPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(
        this.administrativeDistrictIds,
        other.administrativeDistrictIds,
        (a, b) => a.value === b.value,
      )
    );
  }

  override validationToText(): string {
    if (this.administrativeDistrictIds.length === 0) {
      return 'Mindestens einen Landkreis auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdAdministrativeDistrictIdCondition(this.administrativeDistrictIds);
  }

  toJSON(): FilterConditionAdministrativeDistrictIdDto | null {
    return {
      type: this.type,
      administrativeDistrictIds: this.administrativeDistrictIds.map((it) =>
        it.getNumericValue(),
      ),
    };
  }

  toText(): string | null {
    return this.administrativeDistrictIds.length === 1
      ? 'ein Landkreis'
      : `${this.administrativeDistrictIds.length} Landkreise`;
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (this.administrativeDistrictIds.length === 0) return {};
    return {
      [FILTER_RESOURCE_ADMINISTRATIVE_DISTRICT]: this.administrativeDistrictIds.map(
        (it) => it.value,
      ),
    };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    if (this.administrativeDistrictIds.length === 0) return { title };
    const shown = this.administrativeDistrictIds.slice(
      0,
      ADMINISTRATIVE_DISTRICTS_DETAIL_LIMIT,
    );
    const items = shown.map(
      (id) => resolvedNames?.get(id.value) ?? id.value,
    );
    const remaining = this.administrativeDistrictIds.length - shown.length;
    if (remaining > 0) {
      items.push(`… und ${remaining} weitere Landkreise`);
    }
    return { title, items };
  }

  toValue(): BdAdministrativeDistrictId[] {
    return this.administrativeDistrictIds;
  }
}

export class BdAdministrativeDistrictIdCondition extends BdContentCondition<number> {
  constructor(
    includes?: BdAdministrativeDistrictId[],
    excludes?: BdAdministrativeDistrictId[],
  ) {
    super(
      BD_ADMINISTRATIVE_DISTRICT_IDS_CONDITION_ID,
      includes ? includes.map((it) => it.getNumericValue()) : null,
      excludes ? excludes.map((it) => it.getNumericValue()) : null,
    );
  }
}

interface FilterConditionAdministrativeDistrictIdDto {
  type: string;
  administrativeDistrictIds: number[];
}
